import { Dynamic } from "solid-js/web";
import type { ComponentProps } from "solid-js";
import { cn } from "@space/shared";

/**
 * Space Academy brand mark — un libro abierto con una trayectoria ascendente que
 * culmina en una estrella de cuatro puntas: la órbita del que aprende.
 *
 * Monocromo por diseño (currentColor): el mark hereda el color del contexto y se
 * usa siempre sobre el chip sólido de marca (`bg-accent text-accent-foreground`,
 * patrón logo-theme-safe) o directamente en `text-foreground`. Sin gradientes ni
 * caras: identidad profesional, legible de 16px (favicon) a tamaño póster.
 */
export function LogoMark(props: ComponentProps<"svg">) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      {/* Libro abierto */}
      <path
        d="M4.5 17.5C8.5 15.9 13 16.1 16 18.2C19 16.1 23.5 15.9 27.5 17.5L27.5 24.5C23.5 22.9 19 23.1 16 25.2C13 23.1 8.5 22.9 4.5 24.5Z"
        fill="currentColor"
      />
      {/* Trayectoria ascendente */}
      <path
        d="M5.5 13C8 7.6 14.5 4.4 21.5 5.2"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
      />
      {/* Estrella (cuatro puntas) */}
      <path
        d="M26 1.8l1.1 2.85 2.85 1.1-2.85 1.1-1.1 2.85-1.1-2.85-2.85-1.1 2.85-1.1Z"
        fill="currentColor"
      />
    </svg>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   */
  as?: "h1" | "p";
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <span class="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground shadow-sm">
            <LogoMark class="h-5 w-5" />
          </span>
          <span class="space-wordmark text-lg leading-none">
            Space
            <span class="block text-[0.66em] font-semibold tracking-[0.14em] uppercase text-muted">
              Academy
            </span>
          </span>
        </>
      ) : (
        <>
          <span class="mb-2 grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-md transition-transform duration-300 hover:scale-105 active:scale-95">
            <LogoMark class="h-10 w-10" />
          </span>
          <Dynamic component={wordmarkTag()} class="space-wordmark text-3xl">
            Space Academy
          </Dynamic>
        </>
      )}
    </div>
  );
}
